import { 
  doc, 
  getDoc, 
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';

const COLLECTION_NAME = 'rushdSettings';
const SETTINGS_DOC_ID = 'main';

export interface RushdSettings {
  programActive: boolean;
  memberExpressPriceMode: 'percentage_off' | 'flat_price';
  memberRateValue: number;
  minOrderValueEnabled: boolean;
  minOrderValue: number;
  monthlyUsageLimitEnabled: boolean;
  monthlyUsageLimit: number;
  effectiveFrom?: Date;
  lastModifiedBy?: string;
  lastModifiedAt?: Date;
}

const DEFAULT_RUSHD_SETTINGS: RushdSettings = {
  programActive: false,
  memberExpressPriceMode: 'percentage_off',
  memberRateValue: 25,
  minOrderValueEnabled: false,
  minOrderValue: 50,
  monthlyUsageLimitEnabled: false,
  monthlyUsageLimit: 10,
};

/**
 * Get Rush'd settings
 */
export const getRushdSettings = async (): Promise<RushdSettings> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, SETTINGS_DOC_ID);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as any;
      return {
        ...data,
        effectiveFrom: data.effectiveFrom?.toDate ? data.effectiveFrom.toDate() : data.effectiveFrom,
        lastModifiedAt: data.lastModifiedAt?.toDate ? data.lastModifiedAt.toDate() : data.lastModifiedAt,
      } as RushdSettings;
    } else {
      // Return default settings if document doesn't exist
      return DEFAULT_RUSHD_SETTINGS;
    }
  } catch (error) {
    console.error('Error fetching Rush\'d settings:', error);
    throw new Error('Failed to fetch Rush\'d settings');
  }
};

/**
 * Update Rush'd settings
 */
export const updateRushdSettings = async (
  settings: RushdSettings,
  modifiedBy: string
): Promise<RushdSettings> => {
  try {
    const docRef = doc(db, COLLECTION_NAME, SETTINGS_DOC_ID);
    
    const updatedSettings: RushdSettings = {
      ...settings,
      lastModifiedBy: modifiedBy,
      lastModifiedAt: serverTimestamp() as any,
    };
    
    // Remove undefined fields to prevent Firebase errors
    const settingsToSave = Object.fromEntries(
      Object.entries(updatedSettings).filter(([_, value]) => value !== undefined)
    );
    
    await setDoc(docRef, settingsToSave, { merge: true });
    
    // Return the updated settings with current timestamp
    return {
      ...updatedSettings,
      lastModifiedAt: new Date(),
    };
  } catch (error) {
    console.error('Error updating Rush\'d settings:', error);
    throw new Error('Failed to update Rush\'d settings');
  }
};

/**
 * Validate Rush'd settings
 */
export const validateRushdSettings = (settings: RushdSettings): string[] => {
  const errors: string[] = [];
  
  if (settings.memberRateValue <= 0) {
    errors.push('Member rate value must be greater than 0');
  }
  
  if (settings.memberExpressPriceMode === 'percentage_off' && settings.memberRateValue > 100) {
    errors.push('Percentage discount cannot exceed 100%');
  }
  
  if (settings.minOrderValueEnabled && settings.minOrderValue <= 0) {
    errors.push('Minimum order value must be greater than 0');
  }
  
  if (settings.monthlyUsageLimitEnabled && settings.monthlyUsageLimit <= 0) {
    errors.push('Monthly usage limit must be greater than 0');
  }
  
  return errors;
};